import { useState } from 'react';
import type { ItemWithClaims, ClaimWithParticipant } from '@trueup/shared';
import { formatPrice, receiptFont, fractionLabel } from '../lib/formatting';
import { colors } from '../lib/theme';
import { getColorForName, getInitials } from '../lib/profileColors';

// Thermal paper background from theme
const thermalPaperBg = colors.thermalPaper;

// Profile bubble component - matches iOS ProfileBubble exactly
// iOS: font(.system(size: 10, weight: .bold)), foregroundColor(.black.opacity(0.8))
function ProfileBubble({ name, size = 28 }: { name: string; size?: number }) {
  const bgColor = getColorForName(name);
  // iOS uses size 10 for 28px bubble, scale proportionally
  const fontSize = size === 28 ? 10 : Math.round(size * 0.36);
  return (
    <div
      className="rounded-full flex items-center justify-center font-bold border-2 border-white"
      style={{
        width: size,
        height: size,
        fontSize,
        backgroundColor: bgColor,
        color: 'rgba(0, 0, 0, 0.8)', // iOS: .black.opacity(0.8)
      }}
    >
      {getInitials(name)}
    </div>
  );
}

// Claimers bubble display - clicking opens drawer (matching iOS)
function ClaimerBubbles({
  claims,
  onClick,
}: {
  claims: ClaimWithParticipant[];
  onClick?: () => void;
}) {
  if (claims.length === 0) return null;

  return (
    <button
      onClick={onClick}
      className="flex items-center -space-x-2 cursor-pointer"
    >
      {claims.slice(0, 3).map((claim, index) => (
        <div key={claim.id} style={{ zIndex: 3 - index }}>
          <ProfileBubble name={claim.participant.name} size={28} />
        </div>
      ))}
      {claims.length > 3 && (
        <div
          className="w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center text-xs font-medium text-gray-600 border-2 border-white"
          style={{ zIndex: 0 }}
        >
          +{claims.length - 3}
        </div>
      )}
    </button>
  );
}

// Claim drawer component
function ClaimDrawer({
  itemAmount,
  myClaim,
  otherClaims,
  onClaimFraction,
  onUnclaimFraction,
}: {
  itemAmount: number;
  myClaim: ClaimWithParticipant | undefined;
  otherClaims: ClaimWithParticipant[];
  onClaimFraction: (numerator: number, denominator: number) => void;
  onUnclaimFraction: () => void;
}) {
  // Calculate remaining fraction available (not including my claim)
  const totalOthersClaimed = otherClaims.reduce(
    (sum, c) => sum + (c.portionNumerator ?? 1) / (c.portionDenominator ?? 1),
    0
  );
  const availableToMe = 1 - totalOthersClaimed;
  const isFullyClaimedByOthers = availableToMe < 0.01;

  // Current selection state
  const currentDenominator = myClaim?.portionDenominator ?? 1;
  const isWhole = myClaim && currentDenominator === 1;
  const isFractional = myClaim && currentDenominator > 1;

  // Calculate the minimum valid denominator (where 1/n <= availableToMe)
  const minDenominator = Math.ceil(1 / availableToMe);

  // Calculate the amount for current selection
  const currentAmount = myClaim ? Math.round(itemAmount / currentDenominator) : 0;

  const handleWholeClick = () => {
    if (isWhole) {
      onUnclaimFraction();
    } else if (availableToMe >= 0.99) {
      onClaimFraction(1, 1);
    }
  };

  const handleFractionalClick = () => {
    if (isFractional) {
      onUnclaimFraction();
    } else {
      // Start with the minimum valid denominator (or 2 if whole is available)
      const startDenom = availableToMe >= 0.99 ? 2 : minDenominator;
      onClaimFraction(1, startDenom);
    }
  };

  const handleDenominatorChange = (delta: number) => {
    const newDenom = currentDenominator + delta;
    if (newDenom >= minDenominator && newDenom <= 20) {
      onClaimFraction(1, newDenom);
    }
  };

  // If fully claimed by others, just show who claimed it
  if (isFullyClaimedByOthers) {
    return (
      <div className="mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
        <div className="text-xs text-gray-500 uppercase tracking-wide mb-2">Claimed by</div>
        {otherClaims.map((claim) => (
          <div key={claim.id} className="flex items-center gap-2 py-1">
            <ProfileBubble name={claim.participant.name} size={20} />
            <span className="text-sm text-gray-700 flex-1">{claim.participant.name}</span>
            <span className="text-xs text-gray-500">{fractionLabel(claim.portionDenominator)}</span>
            <span className="text-sm text-gray-600" style={receiptFont}>
              {formatPrice(Math.round(itemAmount / claim.portionDenominator))}
            </span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="mt-1 p-3 rounded-lg" style={{ backgroundColor: 'rgba(128, 128, 128, 0.06)', border: '1px solid rgba(128, 128, 128, 0.15)' }}>
      {/* Other claimers section */}
      {otherClaims.length > 0 && (
        <div className="mb-3 pb-3 border-b border-gray-200">
          <div className="text-xs text-gray-500 uppercase tracking-wide mb-2">Others</div>
          {otherClaims.map((claim) => (
            <div key={claim.id} className="flex items-center gap-2 py-1">
              <ProfileBubble name={claim.participant.name} size={20} />
              <span className="text-sm text-gray-700">{claim.participant.name}</span>
              <span className="text-xs text-gray-500">{fractionLabel(claim.portionDenominator)}</span>
              <span className="text-sm text-gray-600" style={receiptFont}>
                {formatPrice(Math.round(itemAmount / claim.portionDenominator))}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Selection options - iOS layout */}
      <div className="flex flex-col gap-2">
        {/* First row: Whole item + Your portion */}
        <div className="flex items-center justify-between">
          {/* Whole item option - only if available */}
          {availableToMe >= 0.99 && (
            <button
              onClick={handleWholeClick}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all ${
                isWhole
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                isWhole ? 'border-blue-500' : 'border-gray-300'
              }`}>
                {isWhole && <div className="w-2 h-2 rounded-full bg-blue-500" />}
              </div>
              <span className="text-sm font-medium">Whole item</span>
            </button>
          )}

          {/* Your portion - right aligned */}
          {myClaim && (
            <div className="text-right">
              <div className="text-xs text-gray-500 uppercase tracking-wide">Your portion</div>
              <div className="text-lg font-bold text-blue-600" style={receiptFont}>
                {formatPrice(currentAmount)}
              </div>
            </div>
          )}
        </div>

        {/* Second row: Fractional option with stepper */}
        <div className="flex items-center gap-2">
          <button
            onClick={handleFractionalClick}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all ${
              isFractional
                ? 'border-blue-500 bg-blue-50 text-blue-700'
                : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
          >
            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
              isFractional ? 'border-blue-500' : 'border-gray-300'
            }`}>
              {isFractional && <div className="w-2 h-2 rounded-full bg-blue-500" />}
            </div>
            <span className="text-sm font-medium whitespace-nowrap">
              Only {fractionLabel(isFractional ? currentDenominator : (availableToMe >= 0.99 ? 2 : minDenominator))}
            </span>
          </button>

          {/* Stepper buttons - only shown when fractional is selected */}
          {/* iOS: font 14pt semibold, padding h:14 v:8, rounded 8, gray.opacity(0.12) bg */}
          {isFractional && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleDenominatorChange(1)}
                disabled={currentDenominator >= 20}
                className="rounded-lg flex items-center justify-center font-semibold transition-all"
                style={{
                  fontSize: 14,
                  paddingLeft: 14,
                  paddingRight: 14,
                  paddingTop: 8,
                  paddingBottom: 8,
                  color: currentDenominator >= 20 ? 'rgba(128, 128, 128, 0.4)' : 'rgba(0, 0, 0, 0.87)',
                  backgroundColor: currentDenominator >= 20 ? 'rgba(128, 128, 128, 0.06)' : 'rgba(128, 128, 128, 0.12)',
                  cursor: currentDenominator >= 20 ? 'not-allowed' : 'pointer',
                }}
              >
                Less
              </button>
              <button
                onClick={() => handleDenominatorChange(-1)}
                disabled={currentDenominator <= minDenominator}
                className="rounded-lg flex items-center justify-center font-semibold transition-all"
                style={{
                  fontSize: 14,
                  paddingLeft: 14,
                  paddingRight: 14,
                  paddingTop: 8,
                  paddingBottom: 8,
                  color: currentDenominator <= minDenominator ? 'rgba(128, 128, 128, 0.4)' : 'rgba(0, 0, 0, 0.87)',
                  backgroundColor: currentDenominator <= minDenominator ? 'rgba(128, 128, 128, 0.06)' : 'rgba(128, 128, 128, 0.12)',
                  cursor: currentDenominator <= minDenominator ? 'not-allowed' : 'pointer',
                }}
              >
                More
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================
// Receipt Item List Container
// ============================================
interface ReceiptItemListProps {
  children: React.ReactNode;
}

export function ReceiptItemList({ children }: ReceiptItemListProps) {
  return (
    <div className="overflow-hidden" style={{ backgroundColor: thermalPaperBg }}>
      {/* Items - iOS style without column headers */}
      <div className="divide-y divide-gray-100">
        {children}
      </div>
    </div>
  );
}

// ============================================
// Single Item Card (quantity = 1)
// ============================================
interface SingleItemCardProps {
  item: ItemWithClaims;
  currentParticipantId: string;
  onClaimFraction: (itemId: string, numerator: number, denominator: number) => void;
  onUnclaimFraction: (itemId: string) => void;
  isClaimingOpen?: boolean;
}

export function SingleItemCard({
  item,
  currentParticipantId,
  onClaimFraction,
  onUnclaimFraction,
  isClaimingOpen = true,
}: SingleItemCardProps) {
  const [showDrawer, setShowDrawer] = useState(false);

  const myClaim = item.claims.find(
    (c) => c.participantId === currentParticipantId && c.unitNumber == null
  );
  const isClaimed = !!myClaim;

  // Get claims from others (fractional claims only)
  const otherClaims = item.claims.filter(
    (c) => c.participantId !== currentParticipantId && c.unitNumber == null
  );

  // Calculate total fraction claimed by others
  const totalOthersClaimed = otherClaims.reduce(
    (sum, c) => sum + (c.portionNumerator ?? 1) / (c.portionDenominator ?? 1),
    0
  );
  const isFullyClaimedByOthers = totalOthersClaimed >= 0.99;

  // Note: No click-outside handler - drawers stay open until explicitly closed (matching iOS)

  // Handle claim button click
  const handleClaimClick = () => {
    if (!isClaimed && !showDrawer) {
      // Only auto-claim whole item if no one else has claimed it
      // Otherwise just open drawer to let user choose their portion
      if (otherClaims.length === 0) {
        onClaimFraction(item.id, 1, 1);
      }
    }
    setShowDrawer(true);
  };

  // Handle claimed button click
  const handleClaimedClick = () => {
    if (showDrawer) {
      setShowDrawer(false);
    } else {
      setShowDrawer(true);
    }
  };

  // Get button text
  const getButtonText = () => {
    if (isClaimed && myClaim) {
      if (myClaim.portionDenominator === 1) {
        return 'Claimed';
      }
      return `Claimed ${fractionLabel(myClaim.portionDenominator)}`;
    }
    return 'Claim';
  };

  // Get the claimer display
  // iOS: caption monospace (~12pt), medium weight, px 12 py 6, Capsule, shadow(black 0.12, radius 2, y 1)
  const buttonShadow = '0 1px 2px rgba(0, 0, 0, 0.12)';

  const getClaimerDisplay = () => {
    if (isClaimed) {
      return isClaimingOpen ? (
        <button
          onClick={handleClaimedClick}
          className="font-semibold rounded-full hover:opacity-90 transition-opacity flex items-center justify-center"
          style={{
            ...receiptFont,
            fontSize: 14,
            paddingLeft: 14,
            paddingRight: 14,
            paddingTop: 8,
            paddingBottom: 8,
            backgroundColor: colors.claimedGreen.bg,
            color: colors.claimedGreen.text,
            boxShadow: buttonShadow,
          }}
        >
          {getButtonText()}
        </button>
      ) : (
        <span
          className="font-semibold rounded-full flex items-center justify-center"
          style={{
            ...receiptFont,
            fontSize: 14,
            paddingLeft: 14,
            paddingRight: 14,
            paddingTop: 8,
            paddingBottom: 8,
            backgroundColor: colors.claimedGreen.bg,
            color: colors.claimedGreen.text,
          }}
        >
          {getButtonText()}
        </span>
      );
    }
    // Show profile bubbles for items claimed by others - clicking toggles drawer
    if (otherClaims.length > 0 && isFullyClaimedByOthers) {
      return <ClaimerBubbles claims={otherClaims} onClick={() => setShowDrawer(!showDrawer)} />;
    }
    // Show Claim button if there's room to claim
    if (isClaimingOpen && !isFullyClaimedByOthers) {
      return (
        <button
          onClick={handleClaimClick}
          className="bg-blue-500 text-white font-semibold rounded-full hover:bg-blue-600 transition-colors flex items-center justify-center"
          style={{
            ...receiptFont,
            fontSize: 14,
            paddingLeft: 14,
            paddingRight: 14,
            paddingTop: 8,
            paddingBottom: 8,
            boxShadow: buttonShadow,
          }}
        >
          Claim
        </button>
      );
    }
    return <span className="text-gray-400">-</span>;
  };

  return (
    <div className="px-4 py-2">
      {/* iOS-style layout */}
      <div className="flex flex-col items-end gap-2">
        {/* First row: Item name ... dotted line ... price */}
        {/* iOS: body font is 17pt, .foregroundColor(.primary) for all items - NOT green */}
        <div className="w-full flex items-end" style={receiptFont}>
          <span className="whitespace-nowrap text-gray-900" style={{ fontSize: '17px' }}>
            {item.name}
          </span>
          <div className="flex-1 border-b border-dotted border-gray-300 mx-2 min-w-4 mb-1" />
          <span className="whitespace-nowrap text-gray-900" style={{ fontSize: '17px' }}>
            {formatPrice(item.amount)}
          </span>
        </div>

        {/* Second row: Claim button (right-aligned) */}
        <div className="flex justify-end">
          {getClaimerDisplay()}
        </div>

        {/* Claim drawer */}
        {showDrawer && isClaimingOpen && (
          <div className="w-full">
            <ClaimDrawer
              itemAmount={item.amount}
              myClaim={myClaim}
              otherClaims={otherClaims}
              onClaimFraction={(num, denom) => onClaimFraction(item.id, num, denom)}
              onUnclaimFraction={() => onUnclaimFraction(item.id)}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================
// Multi-Quantity Item Card (quantity > 1)
// ============================================
interface MultiQuantityItemCardProps {
  item: ItemWithClaims;
  currentParticipantId: string;
  onClaimUnit: (itemId: string, unitNumber: number) => void;
  onUnclaimUnit: (itemId: string, unitNumber: number) => void;
  isClaimingOpen?: boolean;
}

export function MultiQuantityItemCard({
  item,
  currentParticipantId,
  onClaimUnit,
  onUnclaimUnit,
  isClaimingOpen = true,
}: MultiQuantityItemCardProps) {
  const [expandedUnit, setExpandedUnit] = useState<number | null>(null);
  const unitPrice = Math.round(item.amount / item.quantity);
  const claimedUnits = item.claims.filter((c) => c.unitNumber != null).length;
  const claimedAmount = claimedUnits * unitPrice;
  const progressPercent = (claimedUnits / item.quantity) * 100;

  const handleClaimClick = (unitNumber: number) => {
    const unitClaim = item.claims.find((c) => c.unitNumber === unitNumber);

    if (!unitClaim) {
      // Unclaimed: claim it
      onClaimUnit(item.id, unitNumber);
    }
    setExpandedUnit(unitNumber);
  };

  const handleClaimedClick = (unitNumber: number) => {
    if (expandedUnit === unitNumber) {
      setExpandedUnit(null);
    } else {
      setExpandedUnit(unitNumber);
    }
  };

  return (
    <div className="px-4 py-2">
      {/* Header row - iOS style */}
      <div className="flex flex-col gap-2" style={receiptFont}>
        {/* Item name ... dotted line ... price (with quantity indicator) */}
        <div className="flex items-end">
          <span className="text-gray-900 whitespace-nowrap" style={{ fontSize: '17px' }}>{item.name}</span>
          <span className="text-sm text-gray-500 ml-1">(x{item.quantity})</span>
          <div className="flex-1 border-b border-dotted border-gray-300 mx-2 min-w-4 mb-1" />
          <span className="text-gray-900 whitespace-nowrap" style={{ fontSize: '17px' }}>{formatPrice(item.amount)}</span>
        </div>

        {/* Progress bar */}
        <div className="flex items-center gap-2">
          <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-green-500 rounded-full transition-all duration-300"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <span className="text-xs text-gray-500 whitespace-nowrap">
            {formatPrice(claimedAmount)}/{formatPrice(item.amount)}
          </span>
        </div>
      </div>

      {/* Individual units */}
      {Array.from({ length: item.quantity }, (_, i) => i + 1).map((unitNumber, index) => {
        const unitClaim = item.claims.find((c) => c.unitNumber === unitNumber);
        const isMyClaim = unitClaim?.participantId === currentParticipantId;
        const isLast = index === item.quantity - 1;
        const isExpanded = expandedUnit === unitNumber;

        const getClaimerDisplay = () => {
          if (isMyClaim) {
            return isClaimingOpen ? (
              <button
                onClick={() => handleClaimedClick(unitNumber)}
                className="rounded-full hover:bg-green-200 transition-colors flex items-center justify-center font-semibold"
                style={{ ...receiptFont, fontSize: 14, paddingLeft: 14, paddingRight: 14, paddingTop: 8, paddingBottom: 8, backgroundColor: colors.claimedGreen.bg, color: colors.claimedGreen.text }}
              >
                Claimed
              </button>
            ) : (
              <span className="rounded-full flex items-center justify-center font-semibold" style={{ ...receiptFont, fontSize: 14, paddingLeft: 14, paddingRight: 14, paddingTop: 8, paddingBottom: 8, backgroundColor: colors.claimedGreen.bg, color: colors.claimedGreen.text }}>
                Claimed
              </span>
            );
          }
          // Show profile bubble for unit claimed by someone else - clicking toggles expanded
          if (unitClaim) {
            return <ClaimerBubbles claims={[unitClaim]} onClick={() => setExpandedUnit(isExpanded ? null : unitNumber)} />;
          }
          if (isClaimingOpen) {
            return (
              <button
                onClick={() => handleClaimClick(unitNumber)}
                className="bg-blue-500 text-white rounded-full hover:bg-blue-600 transition-colors flex items-center justify-center font-semibold"
                style={{ ...receiptFont, fontSize: 14, paddingLeft: 14, paddingRight: 14, paddingTop: 8, paddingBottom: 8 }}
              >
                Claim
              </button>
            );
          }
          return <span className="text-gray-400">-</span>;
        };

        return (
          <div key={unitNumber} className="pl-4 mt-2">
            {/* iOS-style unit row */}
            <div className="flex flex-col items-end gap-2" style={receiptFont}>
              {/* Unit label ... dotted line ... price */}
              <div className="w-full flex items-end">
                <span className="text-gray-400 mr-2">{isLast ? '└─' : '├─'}</span>
                <span className={`whitespace-nowrap ${isMyClaim ? 'text-green-600' : 'text-gray-700'}`} style={{ fontSize: '15px' }}>
                  {unitNumber} of {item.quantity}
                </span>
                <div className="flex-1 border-b border-dotted border-gray-300 mx-2 min-w-4 mb-1" />
                <span className={`whitespace-nowrap ${isMyClaim ? 'text-green-600' : 'text-gray-600'}`} style={{ fontSize: '15px' }}>
                  {formatPrice(unitPrice)}
                </span>
              </div>

              {/* Claim button (right-aligned) */}
              <div className="flex justify-end">
                {getClaimerDisplay()}
              </div>

              {/* Unit drawer for unclaiming */}
              {isExpanded && isMyClaim && isClaimingOpen && (
                <div className="w-full mt-1 p-3 rounded-lg" style={{ backgroundColor: 'rgba(128, 128, 128, 0.06)', border: '1px solid rgba(128, 128, 128, 0.15)' }}>
                  <button
                    onClick={() => {
                      onUnclaimUnit(item.id, unitNumber);
                      setExpandedUnit(null);
                    }}
                    className="px-4 py-2 border-2 border-gray-200 bg-white rounded-lg hover:border-gray-300 transition-all"
                  >
                    <span className="text-sm font-medium">Unclaim</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ============================================
// Legacy exports for backwards compatibility
// ============================================
interface ItemCardProps {
  item: ItemWithClaims;
  currentParticipantId: string;
  onClaim: (itemId: string) => void;
  onUnclaim: (itemId: string) => void;
  isClaimingOpen?: boolean;
}

export default function ItemCard({
  item,
  currentParticipantId,
  onClaim,
  onUnclaim,
  isClaimingOpen = true,
}: ItemCardProps) {
  return (
    <SingleItemCard
      item={item}
      currentParticipantId={currentParticipantId}
      onClaimFraction={(id) => onClaim(id)}
      onUnclaimFraction={(id) => onUnclaim(id)}
      isClaimingOpen={isClaimingOpen}
    />
  );
}

export function UnrolledItemCard({
  item,
  currentParticipantId,
  onClaimUnit,
  onUnclaimUnit,
  isClaimingOpen = true,
}: {
  item: ItemWithClaims;
  currentParticipantId: string;
  onClaimUnit: (itemId: string, unitNumber: number) => void;
  onUnclaimUnit: (itemId: string, unitNumber: number) => void;
  isClaimingOpen?: boolean;
}) {
  return (
    <MultiQuantityItemCard
      item={item}
      currentParticipantId={currentParticipantId}
      onClaimUnit={onClaimUnit}
      onUnclaimUnit={onUnclaimUnit}
      isClaimingOpen={isClaimingOpen}
    />
  );
}

export function FractionalItemCard({
  item,
  currentParticipantId,
  onClaimFraction,
  onUnclaimFraction,
  isClaimingOpen = true,
}: {
  item: ItemWithClaims;
  currentParticipantId: string;
  onClaimFraction: (itemId: string, numerator: number, denominator: number) => void;
  onUnclaimFraction: (itemId: string) => void;
  isClaimingOpen?: boolean;
}) {
  return (
    <SingleItemCard
      item={item}
      currentParticipantId={currentParticipantId}
      onClaimFraction={onClaimFraction}
      onUnclaimFraction={onUnclaimFraction}
      isClaimingOpen={isClaimingOpen}
    />
  );
}

// Keep ReceiptContainer for backwards compatibility but it's deprecated
export function ReceiptContainer({ children }: { children: React.ReactNode; [key: string]: any }) {
  return <ReceiptItemList>{children}</ReceiptItemList>;
}

// Export utilities for consistent bubble colors (re-export from lib)
export { getColorForName, getInitials, bubbleColors } from '../lib/profileColors';
export { ProfileBubble };
